import {Injectable} from '@angular/core';
import {DataService} from "../data.service";
import {BehaviorSubject, firstValueFrom, forkJoin, Observable, Subject, take} from "rxjs";
import {RookieDistrictServer} from "../../interfaces/rookie/rookie-district-server";
import {Store} from "@ngrx/store";
import {AppState} from "../../store";
import {resetRookieDistrictScopedFilters} from "../../store/actions/filter.actions";
import {RookieDistrictFullData} from "../../interfaces/rookie/rookie-district-full-data";
import {RookieSchool} from "../../interfaces/rookie/rookie-school";
import Schwaz from "../../../assets/text-files/rookie/schwaz";
import Lienz from "../../../assets/text-files/rookie/lienz";
import Imst from "../../../assets/text-files/rookie/imst";

/*
How to add new district:
1. Create new file in text-files/rookie/****.ts (copy any sibling)
2. Add import here
3. Add to _allDistricts array here
 */

@Injectable({
  providedIn: 'root'
})
export class RookieDistrictsService {

  private readonly LOCAL_STORAGE_DISTRICT_KEY = "rookieDistrictName";

  private _currentDistrictId = -1;

  private _allDistricts = [
    Schwaz, Lienz, Imst
  ]
  private _currentDistrictData?: RookieDistrictFullData;

  private _onDistrictUpdated = new Subject<number>();

  // Replayable counterpart of _onDistrictUpdated: _onDistrictUpdated is a plain Subject, so
  // anything constructed after setupDistrict() (root services, lazily created components)
  // never learns which district is current. Consumers that need the *current* value on
  // subscribe read this instead.
  private _currentDistrictId$ = new BehaviorSubject<number>(-1);
  readonly currentDistrictId$ = this._currentDistrictId$.asObservable();

  private districtSchools: RookieSchool[] = [];

  constructor(private dataService: DataService, private store: Store<AppState>) {
  }

  getAllServerDistricts(): Promise<RookieDistrictServer[]> {
    return new Promise<RookieDistrictServer[]>((resolve, reject) => {
      this.dataService.getRookieDistricts().pipe(take(1)).subscribe((response) => {
        resolve(response);
      })
    })
  }

  getAllDistricts(): Promise<RookieDistrictFullData[]> {
    return new Promise<RookieDistrictFullData[]>((resolve, reject) => {
      this.dataService.getRookieDistricts().pipe(take(1)).subscribe((response) => {
        let fullDistricts: RookieDistrictFullData[] = response.map((district) => {
          const fullData = this.getFullDistrictById(district.id)

          if (!fullData) throw new Error("District was not found in text-files assets or was not imported to service");
          return fullData;
        });

        resolve(fullDistricts);
      })
    })
  }

  findDistrictByName(name: string): Promise<RookieDistrictServer | null> {
    return new Promise<RookieDistrictServer | null>((resolve, reject) => {
      this.dataService.getRookieDistricts().pipe(take(1)).subscribe((response) => {
        const foundDistrict = response.find((district) => district.name.toLowerCase() === name) ?? null;
        resolve(foundDistrict);
      })
    })
  }

  setupDistrict(id: number) {
    console.log("Setup district");

    // A real switch, not the initial setup on page load. Selections restored from local storage
    // must survive a reload, so only an actual change of district may reset them.
    const isDistrictChange = this._currentDistrictId !== -1 && this._currentDistrictId !== id;

    return new Promise((resolve, reject) => {
      forkJoin({
        districtState: this.dataService.getRookieDistrictState(id),
        districtDateRange: this.dataService.getRookieDistrictDateRange(id),
        timeSlots: this.dataService.getRookieTimeslots(id),
        providerRegisterTime: this.dataService.getRookieProviderRegisterTime(id)
      }).pipe(take(1)).subscribe(({districtState, districtDateRange, timeSlots, providerRegisterTime}) => {
        this._currentDistrictId = id;
        this._currentDistrictData = this.getFullDistrictById(id)!;
        this._currentDistrictData.state = districtState;
        this._currentDistrictData.mainEventFromToString = districtDateRange;
        this._currentDistrictData.providerRegisterTime = providerRegisterTime;
        this._currentDistrictData.timeSlots = timeSlots;

        // Cleared before the request so the previous district's schools are never readable in
        // the meantime; the fetch stays off the critical path (the guard's promise resolves
        // without it) because nothing reads this list synchronously after setup.
        this.districtSchools = [];
        this.dataService.getRookieSchools(id).pipe(take(1)).subscribe(schools => {
          if (id !== this._currentDistrictId) return;
          this.districtSchools = schools;
        });

        // School, time slot, provider and profession ids belong to exactly one district, so
        // carrying them over would filter the new district's results by foreign ids.
        if (isDistrictChange) {
          this.store.dispatch(resetRookieDistrictScopedFilters());
        }

        this._currentDistrictId$.next(id);
        this._onDistrictUpdated.next(Math.random());
        resolve(undefined);
      })
    });

  }

  getCurrentDistrictId(): number {
    return this._currentDistrictId;
  }

  getCurrentDistrictData() {
    return this._currentDistrictData;
  }

  getCurrentDistrictSchools(): RookieSchool[] {
    return this.districtSchools;
  }

  // Which filter the overview pages ("Schnupperplätze", "Anbieter") offer depends on the district:
  // Schwaz picks a school, Lienz picks one or more time slots ("Zeitraum").
  usesSchoolFilter(): boolean {
    return this.getCurrentDistrictData()?.eventType === 1;
  }

  usesTimeslotFilter(): boolean {
    return this.getCurrentDistrictData()?.eventType === 2;
  }

  getFullDistrictById(id: number) {
    let districtTextData = this._allDistricts.find((el) => el.id === id);
    if (districtTextData === undefined) {
      return undefined;
    }

    return districtTextData;
  }

  onDistrictUpdated(): Observable<number> {
    return this._onDistrictUpdated.asObservable();
  }



  async getSchoolNameBySchoolId(id: number): Promise<string> {
    let name = "";
    if(this.districtSchools.length === 0) {
      this.districtSchools = await firstValueFrom(this.dataService.getRookieSchools(this._currentDistrictId).pipe(take(1)));
    }
    console.log(this.districtSchools);
    const school = this.districtSchools.find((school) => school.id === id);
    if(school) {
      name = school.schoolName;
    }
    return name;
  }

  saveDistrictSelectionToLocalStorage(districtKey: string) {
    const districtByKeyExists = this._allDistricts.find((el) => el.name.toLowerCase() === districtKey);

    if(districtByKeyExists) {
      window.localStorage.setItem(this.LOCAL_STORAGE_DISTRICT_KEY, districtKey);
    }
  }

  getDistrictKeyFromLocalStorage() {
    return window.localStorage.getItem(this.LOCAL_STORAGE_DISTRICT_KEY);
  }
}
